import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

// Hours and minutes as two numeric fields, because an iPad's numeric keypad
// has no colon and a mechanic should not have to switch keyboards to type
// 1060:00. `minutes` is the value in minutes, or null while it is incomplete.
// A change from outside (the autofill) replaces what is shown; typing only
// reports upward, so a half-typed field is never snapped back.
export function HoursField({
  label,
  minutes,
  onChange,
  helperText,
}: {
  label: string
  minutes: number | null
  onChange: (minutes: number | null) => void
  helperText?: string
}) {
  const { t } = useTranslation()
  const [hours, setHours] = useState(minutes == null ? '' : String(Math.floor(minutes / 60)))
  const [mins, setMins] = useState(minutes == null ? '' : String(minutes % 60).padStart(2, '0'))

  const parse = (h: string, m: string) => (h === '' ? null : Number(h) * 60 + Number(m === '' ? '0' : m))

  useEffect(() => {
    // Only when the value really changed from outside; typing already agrees.
    if (minutes === parse(hours, mins)) return
    setHours(minutes == null ? '' : String(Math.floor(minutes / 60)))
    setMins(minutes == null ? '' : String(minutes % 60).padStart(2, '0'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes])

  const update = (h: string, m: string) => {
    setHours(h)
    setMins(m)
    onChange(parse(h, m))
  }

  return (
    <Stack spacing={0.5}>
      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <TextField
          value={hours}
          onChange={(e) => update(e.target.value.replace(/\D/g, '').slice(0, 6), mins)}
          label={t('huolto.hoursUnit')}
          slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
          sx={{ flex: 2 }}
        />
        <Typography variant="h5">:</Typography>
        <TextField
          value={mins}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 2)
            update(hours, Number(v) > 59 ? '59' : v)
          }}
          label={t('huolto.minutesUnit')}
          slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
          sx={{ flex: 1 }}
        />
      </Stack>
      {helperText ? (
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
    </Stack>
  )
}
